import React from "react"
import {
    Box,
    ExternalLink,
    Image,
    componentStyles,
} from "@mallardbay/lib-react-components"
import type { UIMessage } from "@ai-sdk/react"

import ChatAttachmentChip from "~components/shared/chat/chat-attachment-chip"
import ChatAttachmentFileIcon from "~components/shared/chat/chat-attachment-file-icon"
import {
    CHAT_ATTACHMENT_CHIP_BORDER_RADIUS,
    CHAT_ATTACHMENT_MESSAGE_IMAGE_MAX_HEIGHT_PX,
    CHAT_ATTACHMENT_MESSAGE_IMAGE_MAX_WIDTH_PX,
} from "~components/shared/chat/chat-attachment-preview-constants"
import {
    getChatAttachmentPreviewKind,
    type ChatFileAttachment,
} from "~components/shared/chat/chat-file-attachment-helpers"
import useChatAttachmentChipStyles from "~components/shared/chat/use-chat-attachment-chip-styles"
import COPY from "~config/copy-constants"
import { TEST_IDS } from "~config/constants"

const FILE_PART_TYPE = "file"

interface Props {
    readonly part: UIMessage["parts"][number]
}

export default function ChatMessageFile({ part }: Props) {
    const chipStyles = useChatAttachmentChipStyles()
    const styles = useMessageFileStyles()
    if (part.type !== FILE_PART_TYPE) return null

    const attachment = toMessageAttachment(part)
    const previewKind = getChatAttachmentPreviewKind(attachment.mediaType)

    return (
        <Box style={styles.root} testId={TEST_IDS.CHAT_MESSAGE_FILE}>
            {previewKind === "image" ? (
                <Image
                    src={attachment.url}
                    alt={attachment.filename}
                    style={styles.image}
                />
            ) : (
                <ChatAttachmentChip>
                    <ChatAttachmentFileIcon />
                    <ExternalLink
                        href={attachment.url}
                        style={chipStyles.fileLink}
                    >
                        {attachment.filename ||
                            COPY.AI_CHAT_FILE_DOWNLOAD_LABEL}
                    </ExternalLink>
                </ChatAttachmentChip>
            )}
        </Box>
    )
}

function toMessageAttachment(
    part: Extract<UIMessage["parts"][number], { type: "file" }>
): ChatFileAttachment {
    return {
        id: part.url,
        url: part.url,
        filename: part.filename ?? COPY.AI_CHAT_FILE_DOWNLOAD_LABEL,
        mediaType: part.mediaType,
    }
}

function useMessageFileStyles() {
    return componentStyles({
        root: {
            display: "inline-flex",
            maxWidth: "100%",
        },
        image: {
            maxWidth: CHAT_ATTACHMENT_MESSAGE_IMAGE_MAX_WIDTH_PX,
            maxHeight: CHAT_ATTACHMENT_MESSAGE_IMAGE_MAX_HEIGHT_PX,
            borderRadius: CHAT_ATTACHMENT_CHIP_BORDER_RADIUS,
        },
    })
}
